import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export const ENQUIRY_TYPES = [
  "General Enquiry",
  "Corporate Order",
  "Bulk Order",
  "Catering",
  "Birthday / Event",
  "Franchise Enquiry",
  "Other",
];

const blank = { name: "", company: "", phone: "", email: "", enquiry_type: "", message: "" };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const PHONE_RE = /^\+?[0-9][0-9\s-]{6,17}$/;

export const EnquiryForm = () => {
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: "" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;

    const next = {};
    if (form.email.trim() && !EMAIL_RE.test(form.email.trim()))
      next.email = "Please enter a valid email address.";
    if (form.phone.trim() && !PHONE_RE.test(form.phone.trim()))
      next.phone = "Please enter a valid contact number.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setLoading(true);
    try {
      await api.post("/enquiries", form);
      setForm(blank);
      setDone(true);
    } catch (err) {
      setErrors({ form: apiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        data-testid="enquiry-success"
        className="rounded-2xl border border-border bg-card p-8"
      >
        <CheckCircle2 className="h-8 w-8 text-copper" />
        <h3 className="mt-5 font-serif text-2xl text-cream">
          Thank you for contacting JH Brew Bites!
        </h3>
        <p className="mt-3 text-sm text-cream/70 md:text-base">
          We have received your enquiry and our team will get back to you soon.
        </p>
        <Button
          onClick={() => setDone(false)}
          data-testid="enquiry-new-btn"
          className="mt-7 rounded-full bg-copper font-semibold text-charcoal hover:bg-caramel"
        >
          Send another enquiry
        </Button>
      </motion.div>
    );

  return (
    <form onSubmit={submit} data-testid="enquiry-form" className="rounded-2xl border border-border bg-card p-6 md:p-8">
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Name</Label>
          <Input value={form.name} onChange={set("name")} placeholder="Enter your name"
            data-testid="enquiry-name-input" className="mt-2 bg-background" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Company Name</Label>
          <Input value={form.company} onChange={set("company")} placeholder="Enter company name"
            data-testid="enquiry-company-input" className="mt-2 bg-background" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Contact Number</Label>
          <Input value={form.phone} onChange={set("phone")} placeholder="Enter your contact number"
            data-testid="enquiry-phone-input" className="mt-2 bg-background" />
          {errors.phone && (
            <p className="mt-1.5 text-xs text-red-400" data-testid="enquiry-phone-error">{errors.phone}</p>
          )}
        </div>
        <div>
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Email ID</Label>
          <Input value={form.email} onChange={set("email")} placeholder="Enter your email address"
            data-testid="enquiry-email-input" className="mt-2 bg-background" />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-400" data-testid="enquiry-email-error">{errors.email}</p>
          )}
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Enquiry Type</Label>
          <Select value={form.enquiry_type} onValueChange={(v) => setForm({ ...form, enquiry_type: v })}>
            <SelectTrigger className="mt-2 bg-background" data-testid="enquiry-type-select">
              <SelectValue placeholder="Select enquiry type" />
            </SelectTrigger>
            <SelectContent>
              {ENQUIRY_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Message</Label>
          <Textarea value={form.message} onChange={set("message")} rows={4}
            placeholder="Tell us how we can help you..." data-testid="enquiry-message-input"
            className="mt-2 bg-background" />
        </div>
      </div>

      {errors.form && (
        <p className="mt-5 text-sm text-red-400" data-testid="enquiry-form-error">{errors.form}</p>
      )}

      <Button type="submit" disabled={loading} data-testid="enquiry-submit-btn"
        className="mt-7 rounded-full bg-copper px-7 font-semibold text-charcoal hover:bg-caramel">
        <Send className="mr-2 h-4 w-4" />
        {loading ? "Sending…" : "Submit Enquiry"}
      </Button>
    </form>
  );
};
